import { FC, useEffect } from "react";
import BreadCrumbs from "../../../components/common/BreadCrumbs/BreadCrumbs";
import Table from "../../../components/common/Table/Table";
import CardTable from "../../../components/common/CardTable";
import Loader from "../../../components/common/Loader";
import { useNavigate } from "react-router-dom";
import ScrollToTopBtn from "../../../components/common/ScrollToTopBtn";
import Pagination from "../../../components/common/Table/Pagination";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { fetchManagerApplicationsList, setCurrentPage } from "../../../store/slices/Citizenship/managerPortalSlice";
import { fetchProfile } from "../../../store/slices/profileSlice";
import Notifications from "../../../components/common/Notifications";
import ErrorIcon from "../../../../public/assets/images/errorCircleSmall.svg";
import { trackButtonClick, trackNavigation, trackCustomPageView } from '../../../services/analytics';

const ManagerPortal: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { applications, loading, error, currentPage, perPage, totalItems } = useAppSelector(
    (state) => state.managerPortal
  );
  const profile = useAppSelector((state) => state.profile.profile);
  const profileLoading = useAppSelector((state) => state.profile.loading);
  const profileError = useAppSelector((state) => state.profile.profileError);

  interface TableColumn {
    key: string;
    label: string;
    className?: string;
  }

  interface TableRow {
    [key: string]: React.ReactNode;
  }

  const columns: TableColumn[] = [
    { key: "nuid", label: "Employee ID" },
    { key: "empName", label: "Name" },
    { key: "id", label: "Application ID" },
    { key: "applicationDate", label: "Application date" },
    { key: "status", label: "Status" },
    {key :"timeOffDate", label: "Paid day off date"},
    {key:"timeOffNumOfHrs", label: "Paid day off hours"},
    { key: "action", label: "Action" },
  ];

  useEffect(() => {
    if (!profile && !profileLoading && !profileError) {
      dispatch(fetchProfile());
      return;
    }

    // only fetch manager applications when profile indicates manager
    if (profile?.isManager) {
      dispatch(fetchManagerApplicationsList({ page: currentPage, perPage }));
    }
  }, [currentPage, perPage, dispatch, profile, profileLoading, profileError]);

  const rows: TableRow[] = applications.map((row: any) => {
    return {
      ...row,
      status: (
        <span className="bg-gray-100 border border-[#00000014] rounded px-3! py-1 text-sm font-semibold inline-flex items-center justify-center">
          {row.status || "-"}
        </span>
      ),
      timeOffDate: row.timeOffDate ? String(row.timeOffDate) : "mm/dd/yyyy",
      timeOffNumOfHrs: row.timeOffNumOfHrs !== undefined && row.timeOffNumOfHrs !== null ? String(row.timeOffNumOfHrs) : "0",
      action: (
        <button
          onClick={() => handleActionClick(row)}
          className="inline-link medium no-underline! text-[14px]! cursor-pointer hover:underline"
        >
          Add paid day off hours
        </button>
      ),
    };
  });
  const handleActionClick = (rowData: any) => {
    const id = rowData?.id ?? "";
    if (id) {
      console.log(`📊 Analytics: Add paid day off clicked - Application ${id}`);
      trackButtonClick('Add paid day off hours', 'manager-portal-table', { applicationId: id });
      trackCustomPageView('manager-portal:paid-hours');
      navigate(`/citizenship/manager-details/${encodeURIComponent(String(id))}`, {
        state: { nuid: rowData?.nuid ?? null },
      });
      return;
    }
  };

  const handleBreadcrumbPrevious = (): void => {
    console.log('📊 Analytics: Navigate back to citizenship');
    trackNavigation('manager-portal', 'citizenship', 'back');
    navigate("/citizenship");
  };

const handlePageChange = (page: number) => {
  dispatch(setCurrentPage(page));
};

  if (profileLoading || loading) {
    return <Loader />;
  }

  if (profileError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Notifications messageInfo={profileError} status={false} />
      </div>
    );
  }

  return (
    <div className="min-h-screen desktop:mx-40! mx-5!">
      <BreadCrumbs
        previousPage="Citizenship Fee Assistance"
        currentPage="Manager portal"
        onPreviousClick={handleBreadcrumbPrevious}
      />
      <div>
        <h1 className="h1 mt-8!">Manager Portal</h1>
      </div>
      {error && (
        <div className="mt-4!">
          <Notifications messageInfo={error} status={false} />
        </div>
      )}
      <CardTable>
        {profile && !profile.isManager ? (
          <div className="w-full flex items-center justify-center min-h-[300px]">
            <div className="w-full max-w-md">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4">
                  <img src={ErrorIcon} alt="access denied" className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="h3 text-red-600 mb-2">Access denied</h3>
                <p className="text-gray-700 text-sm">You do not have permission to view the Manager Portal.</p>
              </div>
            </div>
          </div>
        ) : rows.length >0 ? (
          <>
            <Table
              columns={columns}
              rows={rows}
              onActionClick={handleActionClick}
              caption="My citizenship fee applications"
              isSorting={false}
              managerPortalMobileOnly={true}
            />
            <div className="mt-4 mobile:relative mobile:left-[30px]">
              <Pagination
                totalItems={totalItems || applications.length}
                itemsPerPage={perPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        ) : (
          <p className="p3 -book text-center mt-2.5! desktop:mt-[200px]!">No applications found.</p>
        )}
      </CardTable>
      <ScrollToTopBtn />
    </div>
  );
};

export default ManagerPortal;

